import { useState, useCallback, useRef } from 'react';
import { checkZipCode } from '../utils/api';
import { debounce, saveZipFlag, getZipFlag } from '../utils/helpers';
import { CapFeedback, ProductType } from '../types';
import { calculateBonusSummary } from '../config/savingsConfig';

interface UseZipCodeCheckReturn {
  zipFlag: boolean | null;
  isChecking: boolean;
  isValid: boolean | null;
  capFeedback: CapFeedback | null;
  checkZip: (zip: string, productType?: ProductType) => void;
}

/**
 * Verifica CAP via API, persistenza zipFlag e feedback numero bonus.
 */
export function useZipCodeCheck(): UseZipCodeCheckReturn {
  const [zipFlag, setZipFlag] = useState<boolean | null>(() => getZipFlag());
  const [isChecking, setIsChecking] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [capFeedback, setCapFeedback] = useState<CapFeedback | null>(null);

  const debouncedCheck = useRef(
    debounce(async (zip: string, productType?: ProductType) => {
      if (!zip || zip.length < 5 || !/^\d{5}$/.test(zip)) {
        setZipFlag(null);
        setIsValid(null);
        setCapFeedback(null);
        saveZipFlag(null);
        setIsChecking(false);
        return;
      }

      setIsChecking(true);

      try {
        const result = await checkZipCode(zip);

        if (result !== null) {
          setZipFlag(result);
          setIsValid(true);
          saveZipFlag(result);

          const summary = calculateBonusSummary(productType, result, zip);
          setCapFeedback({
            bonusCount: summary.bonusCount,
            totalSavings: summary.totalSavings,
            isValid: true,
          });

          document.dispatchEvent(
            new CustomEvent('enpal:zipChecked', {
              detail: { zip, inList: result },
            })
          );
        } else {
          setZipFlag(null);
          setIsValid(false);
          setCapFeedback({
            bonusCount: 0,
            totalSavings: 0,
            isValid: false,
          });
          saveZipFlag(null);
        }
      } catch (error) {
        console.error('Error checking ZIP:', error);
        setZipFlag(null);
        setIsValid(false);
        setCapFeedback(null);
        saveZipFlag(null);
      } finally {
        setIsChecking(false);
      }
    }, 50)
  ).current;

  const checkZip = useCallback(
    (zip: string, productType?: ProductType) => {
      if (zip.length < 5) {
        setIsValid(null);
        setCapFeedback(null);
      }
      debouncedCheck(zip, productType);
    },
    [debouncedCheck]
  );

  return {
    zipFlag,
    isChecking,
    isValid,
    capFeedback,
    checkZip,
  };
}
